import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setTypeaheadUserSelectedDates, setTypeaheadUserSelectedOccupancy } from 'actions/SearchActions'
import { mapOccupanciesToTypeaheadFormat } from 'lib/search/searchUtils'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { rem } from 'polished'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import {
  formatDate,
  getOccupancyDescription,
} from 'components/SearchV2/Utils/GlobalSearchV2'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import TravellersFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/TravellersFloatingPanel'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import SearchTrendingDestinations from 'components/Search/SearchForm/SearchTrendingDestinations'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import { DATE_SEARCH_OPTION_IDS, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { EmptyArray } from 'lib/array/arrayUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from '../../../../../hooks/Optimizely/useOptimizelyExperiment'
import { NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS } from 'constants/naturalLanguageSearch'

/* Extended AIGlowBorder with white background and no border for children */
const StyledAIGlowBorder = styled(AIGlowBorder)`
  > * {
    background: white;
    border: none !important;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

const StyledTrendingDestinations = styled(SearchTrendingDestinations)`
  margin-top: ${rem(24)};
`

export enum searchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
}

interface Props {
  closeSearch?: () => void
  onSearch: () => void
  isInPage?: boolean // i.e. not part of drawer
  updateSearchSessionId?: (newSessionId: string) => void
  naturalLanguageSearch?: boolean
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
}

function HotelsTabContent(props: Props) {
  const {
    closeSearch,
    onSearch,
    isInPage,
    updateSearchSessionId,
    temporarySearchSubmitRef,
    naturalLanguageSearch: naturalLanguageSearchOverride,
  } = props
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const locationLabels = useLocationSearchLabels()

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies = EmptyArray,
    areRoomErrorsShown,
    flexibleMonths,
    flexibleNights,
    dateSearchOptionId,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const { openedSearchVertical, option } = useGlobalSearchURLHashVertical()

  const isDateSelectOpen = openedSearchVertical === 'hotels' && option === 'dateSelect'

  const recentSearches = useRecentSearches()

  const DATE_INPUT_PLACEHOLDER = intl.formatMessage({
    id: 'yTj1l4rcgPlSgXhzjDOF',
    defaultMessage: 'Anytime',
    description: 'Placeholder text for date input when no date is selected',
  })

  const stepsValidation = useMemo(
    () => ({
      [searchSteps.LOCATION]: searchItem,
      [searchSteps.DATE]:
        (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME || (checkinDate && checkoutDate) || flexibleMonths),
    }),
    [searchItem, dateSearchOptionId, checkinDate, checkoutDate, flexibleMonths],
  )

  const flexibleSearchSelectionText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights)

  const [activeStep, setActiveStep] = useState<searchSteps>()
  const { goToNextStep, goToPreviousStep, closeActiveStep, isSearchEnabled } =
    useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const openLocationSearchStep = useCallback(
    () => {
      // Reset location interaction flag when opening the location panel
      // This prevents the panel from auto-closing due to stale state from previous sessions
      setHasUserInteracted(prev => ({ ...prev, location: false }))
      setActiveStep(searchSteps.LOCATION)
    },
    [setActiveStep],
  )
  const openDateSearchStep = useCallback(
    () => setActiveStep(searchSteps.DATE),
    [setActiveStep],
  )
  const openGuestsSearchStep = useCallback(
    () => setActiveStep(searchSteps.GUESTS),
    [setActiveStep],
  )

  useEffect(() => {
    if (isDateSelectOpen) {
      openDateSearchStep()
    }
  }, [isDateSelectOpen, openDateSearchStep])

  const { checkin, checkout } = useMemo(
    () => ({
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    }),
    [checkinDate, checkoutDate],
  )

  const guestsDescription = useMemo(
    () => getOccupancyDescription(occupancies, intl),
    [occupancies, intl],
  )
  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: closeActiveStep,
  })

  const naturalLanguageSearchExperiment = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)
  const naturalLanguageSearch = naturalLanguageSearchOverride ?? naturalLanguageSearchExperiment

  const locationPanelTitle = useMemo(
    () =>
      naturalLanguageSearch
        ? intl.formatMessage({
            id: '_PdNsEJ0XYwldrsZcJ_8',
            defaultMessage: 'Where will you stay?',
            description: 'Title for location search panel in natural language search mode',
          })
        : undefined,
    [intl, naturalLanguageSearch],
  )

  const searchButtonText = intl.formatMessage({
    id: 'NlUtVRbkXOmpSFkheT4Z',
    defaultMessage: 'Search',
    description: 'Search button text for global hotels search on the hotel page',
  })

  const applyButtonText = intl.formatMessage({
    id: 'ENxPPE_4gjNvtXZpYAUq',
    defaultMessage: 'Apply',
    description: 'Apply button text for search form panels in the search interface',
  })

  const whenLabel = intl.formatMessage({
    id: 'G-va_y_Ri6pCw2Ek4uPR',
    defaultMessage: 'When',
    description: 'Label for the date search field input in the main search and discovery interface',
  })

  const checkInLabel = intl.formatMessage({
    id: 'BZImHfWDmEabHe95A4Ie',
    defaultMessage: 'Check in',
    description: 'Form field label for the hotel check-in date in the booking date picker',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'zNj1m3NcaWN8Inz8iuy9',
    defaultMessage: 'Check out',
    description: 'Label for check-out date field in the date selection or calendar picker',
  })

  const guestsLabel = intl.formatMessage({
    id: 'j_s8RUuhdvQTR8vVHX5u',
    defaultMessage: 'Guests',
    description: 'Label for guests/occupancy field in the occupancy or guest picker',
  })

  // Callbacks to track user-selected dates/occupancy for typeahead extract events
  const onUserSelectedDates = useCallback((dates: App.TypeaheadExtractedTravelDates) => {
    const typeaheadId = temporarySearchSubmitRef?.current?.typeaheadId
    if (!typeaheadId) return

    dispatch(setTypeaheadUserSelectedDates({ typeaheadId, dates }))
  }, [dispatch, temporarySearchSubmitRef])

  const onUserSelectedOccupancy = useCallback((rooms: Array<App.Occupants>) => {
    const typeaheadId = temporarySearchSubmitRef?.current?.typeaheadId
    if (!typeaheadId || !rooms || rooms.length === 0) return

    dispatch(setTypeaheadUserSelectedOccupancy({
      typeaheadId,
      occupancy: mapOccupanciesToTypeaheadFormat(rooms),
    }))
  }, [dispatch, temporarySearchSubmitRef])

  const [hasUserInteracted, setHasUserInteracted] = useState({
    dates: false,
    travelers: false,
    location: false,
  })

  const handleUserSelectedLocation = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, location: true }))
  }, [])

  // Reset location flag when advancing from location step
  const handleLocationConfirm = useCallback(() => {
    openDateSearchStep()
    setHasUserInteracted(prev => ({ ...prev, location: false }))
  }, [openDateSearchStep])

  const handleDateStep = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, dates: true }))
    openDateSearchStep()
  }, [openDateSearchStep])

  const handleGuestsStep = useCallback(() => {
    setHasUserInteracted(prev => ({ ...prev, travelers: true }))
    openGuestsSearchStep()
  }, [openGuestsSearchStep])

  const [travelDatesShowAIGlow, setTravelDatesShowAIGlow] = useState(false)

  useEffect(() => {
    if (travelDatesShowAIGlow) {
      const timer = setTimeout(() => {
        setTravelDatesShowAIGlow(false)
      }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

      return () => clearTimeout(timer)
    }
  }, [travelDatesShowAIGlow])

  const [occupancyShowAIGlow, setOccupancyShowAIGlow] = useState(false)

  useEffect(() => {
    if (occupancyShowAIGlow) {
      const timer = setTimeout(() => {
        setOccupancyShowAIGlow(false)
      }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

      return () => clearTimeout(timer)
    }
  }, [occupancyShowAIGlow])

  const locationField = naturalLanguageSearch ? (
    <StyledAIGlowBorder>
      <SearchFormField
        label={locationLabels.titleLabelNaturalLanguage}
        placeholder={locationLabels.placeholderLabelNaturalLanguage}
        value={searchItem?.format.mainText}
        onClick={openLocationSearchStep}
      />
    </StyledAIGlowBorder>
  ) : (
    <SearchFormField
      label={locationLabels.titleLabel}
      placeholder={locationLabels.placeholderLabel}
      value={searchItem?.format.mainText}
      onClick={openLocationSearchStep}
    />
  )

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
        isOpen={activeStep === searchSteps.LOCATION}
        onClose={closeActiveStep}
        onConfirm={handleLocationConfirm}
        onGoBack={goToPreviousStep}
        typeaheadVertical="hotel"
        recentSearches={recentSearches}
        updateSearchSessionId={updateSearchSessionId}
        placeholder={naturalLanguageSearch ? locationLabels.placeholderLabelNaturalLanguage : locationLabels.placeholderLabel}
        multiline={naturalLanguageSearch}
        showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
        label={naturalLanguageSearch ? locationLabels.titleLabelNaturalLanguage : locationLabels.titleLabel}
        title={locationPanelTitle}
        userInteractions={hasUserInteracted}
        setTravelDatesShowAIGlow={setTravelDatesShowAIGlow}
        setOccupancyShowAIGlow={setOccupancyShowAIGlow}
        temporarySearchSubmitRef={temporarySearchSubmitRef}
        onUserSelectedLocation={handleUserSelectedLocation}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? searchButtonText : applyButtonText}
        onUserDatesChanged={onUserSelectedDates}
      />

      <TravellersFloatingPanel
        isOpen={activeStep === searchSteps.GUESTS}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? searchButtonText : applyButtonText}
        onUserSelectedOccupancy={onUserSelectedOccupancy}
      />

      <FieldsWrapper>
        {locationField}

        <SearchFormFieldGroup>
          {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <SearchFormField
              label={whenLabel}
              onClick={handleDateStep}
              placeholder={DATE_INPUT_PLACEHOLDER}
              value={flexibleSearchSelectionText}
              showAIGlow={travelDatesShowAIGlow}
            />
          )}
          {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <>
              <SearchFormField
                label={checkInLabel}
                placeholder={DATE_INPUT_PLACEHOLDER}
                value={checkin}
                onClick={handleDateStep}
                showAIGlow={travelDatesShowAIGlow}
              />
              <SearchFormField
                label={checkOutLabel}
                placeholder={DATE_INPUT_PLACEHOLDER}
                value={checkout}
                onClick={handleDateStep}
                showAIGlow={travelDatesShowAIGlow}
              />
            </>
          )}
        </SearchFormFieldGroup>
        <SearchFormField
          label={guestsLabel}
          value={guestsDescription}
          onClick={handleGuestsStep}
          hasError={areRoomErrorsShown}
          showAIGlow={occupancyShowAIGlow}
        />

      </FieldsWrapper>
      {!isInPage &&
        <StyledTrendingDestinations
          titleColour="neutral-two"
          onClick={closeSearch}
        />}
    </ContentWrapper>
  )
}

export default HotelsTabContent
